import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('TPF_TOKEN'));
  const [loading, setLoading] = useState(true);

  const login = useCallback((newToken, userData) => {
    localStorage.setItem('TPF_TOKEN', newToken);
    setToken(newToken);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('TPF_TOKEN');
    setToken(null);
    setUser(null);
  }, []);

  // apiFetch with the current token; an expired or invalid token logs the user out
  const authFetch = useCallback((path, options = {}) => (
    apiFetch(path, { token, onUnauthorized: logout, ...options })
  ), [token, logout]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setUser(await authFetch('/user/me'));
      } catch (error) {
        if (error.status !== 401) {
          console.error('Failed to fetch user profile:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token, authFetch]);

  const updateUserPreferences = useCallback(async (preferences) => {
    if (!token) return;

    try {
      setUser(await authFetch('/user/preferences', { method: 'PUT', json: preferences }));
      return true;
    } catch (error) {
      console.error('Failed to update user preferences:', error);
      return false;
    }
  }, [token, authFetch]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    authFetch,
    updateUserPreferences,
    isLoggedIn: !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
